import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ProductGrid } from '@/components/pos/ProductGrid';
import { BundleGrid } from '@/components/pos/BundleGrid';
import { BundleBuilder } from '@/components/pos/BundleBuilder';
import { ProductSearchBar } from '@/components/pos/ProductSearchBar';
import { VisitDatePicker } from '@/components/pos/VisitDatePicker';
import { BlockedDateBanner } from '@/components/pos/BlockedDateBanner';
import { CartSidebar } from '@/components/pos/CartSidebar';
import { useCartModeGuard } from '@/components/pos/CartModeGuard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCatalog, useProducts, useBundles, useVisitDateBlock } from '@/hooks/queries';
import { usePriceSync } from '@/hooks/usePriceSync';
import { isFoc, isRegularCatalogProduct } from '@/lib/products';
import { useCartStore } from '@/stores/cart.store';
import { useAuthStore } from '@/stores/auth.store';
import { useHotkeys } from '@/hooks/useHotkeys';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useCatalogAvailability } from '@/hooks/useCatalogAvailability';
import { hideUnavailableSlotTickets } from '@/lib/slotAvailability';
import { formatDate, todayISO } from '@/lib/format';
import { blockedDateMessage } from '@/lib/visitDateErrors';
import type { Product } from '@/types/product';
import type { Bundle } from '@/types/coupon';

/** Semua (both) is the default; Tiket = products only, Paket = bundles only. */
type CatalogFilter = 'all' | 'tickets' | 'bundles';

/** Cashier identity block — who is ringing, on what day. Shared with FocSellPage. */
export function CashierInfo() {
  const user = useAuthStore((s) => s.user);
  const name = user?.name ?? user?.email ?? 'Kasir';
  const initial = name.trim().charAt(0).toUpperCase() || 'K';

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary font-display text-lg font-bold text-cream-50 shadow-sm">
        {initial}
      </div>
      <div className="min-w-0">
        <p className="truncate font-display text-base font-bold leading-tight text-ink-900">
          {name}
        </p>
        <p className="truncate text-xs text-ink-400">{formatDate(todayISO())}</p>
      </div>
    </div>
  );
}

/** Eyebrow label + hairline that heads each section in the "Semua" view. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
        {children}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function SellPage() {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<CatalogFilter>('all');

  const visitDate = useCartStore((s) => s.visitDate);
  const setVisitDate = useCartStore((s) => s.setVisitDate);
  const addProduct = useCartStore((s) => s.addProduct);
  const addBundle = useCartStore((s) => s.addBundle);
  const lineCount = useCartStore((s) => s.lines.length + s.bundleLines.length);
  const clearCart = useCartStore((s) => s.clearCart);
  // A cart still holding FOC tickets asks to be cleared before selling here.
  const { mismatch: modeMismatch, dialog: modeDialog } = useCartModeGuard('regular');

  // Operator-blocked visit date (incl. today on load) → nothing can be sold
  // for it: catalog, barcode, F4 and Lanjutkan are all locked until the
  // cashier picks another date. The BE refuses it too (409 VISIT_DATE_BLOCKED).
  const visitBlock = useVisitDateBlock(visitDate);
  const dateBlocked = visitBlock !== null;

  /** True (and toasts why) when the selected visit date cannot be sold. */
  function rejectIfBlocked(): boolean {
    if (!visitBlock) return false;
    toast.error(blockedDateMessage(visitBlock.reason, visitDate));
    return true;
  }

  // Every product this login may sell, any date. Still needed beside the
  // catalog: the blocked-date view, the FOC hint on a scanned barcode, the
  // "N hidden" count, and the fallback when the catalog cannot be read.
  const { data, isLoading: productsLoading, isError: productsError, refetch } = useProducts();
  // Catalog is parents only — attraction (child) tickets are added per visitor
  // on the Details step, never sold standalone. Inactive products are excluded:
  // /product returns every status, so the POS filters them out itself. FOC
  // tickets are sold only from their own page (/foc), never from here.
  const allProducts = useMemo(
    () => (data?.items ?? []).filter(isRegularCatalogProduct),
    [data],
  );

  // The day's catalog: what is on sale for the visit date, with its promos.
  // The backend already leaves out a slot ticket with nothing bookable that
  // day. Not fetched for a blocked date — nothing sells, and every card stays
  // on screen under its "Tutup" stamp so the cashier sees why.
  const catalog = useCatalog(visitDate, { ticketCategory: 'main', enabled: !dateBlocked });
  // A catalog that cannot be read falls back to the plain list and the
  // browser-side slot check, so the counter keeps selling; only the promo
  // chips are lost.
  const fromCatalog = !dateBlocked && !catalog.isError;
  const products = useMemo(
    () => (fromCatalog ? (catalog.data ?? []).filter(isRegularCatalogProduct) : allProducts),
    [fromCatalog, catalog.data, allProducts],
  );
  const isLoading = productsLoading || (fromCatalog && catalog.isLoading);
  const isError = productsError && !fromCatalog;

  // Per-product availability for the visit date, shared with the barcode scanner
  // so a scanned sold-out / expired ticket is rejected like the card disables it.
  const availabilityByProduct = useCatalogAvailability(products, visitDate);

  // Time-slot tickets with nothing bookable on the visit date (every slot ended,
  // full or closed) are hidden, not greyed out. The catalog already left those
  // out when it was read; this catches a slot that ends while the page is open.
  const sellableProducts = useMemo(
    () => (dateBlocked ? products : hideUnavailableSlotTickets(products, availabilityByProduct)),
    [dateBlocked, products, availabilityByProduct],
  );
  // Counted against every product this login may sell, so the tickets the
  // catalog left out for the day are in it too.
  const hiddenCount = isLoading ? 0 : Math.max(0, allProducts.length - sellableProducts.length);

  const {
    data: bundleData,
    isLoading: bundlesLoading,
    isError: bundlesError,
    refetch: refetchBundles,
  } = useBundles();
  const bundles = useMemo(() => bundleData ?? [], [bundleData]);

  // Date-resolved prices (weekend/holiday/school-holiday rules) so the catalog
  // and cart show what the sale will actually charge, not the weekday base.
  const { productPrices, bundlePrices } = usePriceSync();

  // FLEXIBLE-bundle configuration dialog
  const [bundleConfig, setBundleConfig] = useState<Bundle | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sellableProducts;
    return sellableProducts.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q),
    );
  }, [sellableProducts, query]);

  const filteredBundles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? bundles.filter((b) => b.name.toLowerCase().includes(q)) : bundles;
  }, [bundles, query]);

  const showTickets = filter === 'all' || filter === 'tickets';
  const showBundles = filter === 'all' || filter === 'bundles';

  const hiddenNote = hiddenCount > 0 && filter !== 'bundles' ? ` · ${hiddenCount} habis disembunyikan` : '';
  const countLabel =
    (filter === 'bundles'
      ? `${filteredBundles.length} paket`
      : filter === 'tickets'
        ? `${filtered.length} tiket`
        : `${filtered.length} tiket · ${filteredBundles.length} paket`) + hiddenNote;

  function selectBundle(bundle: Bundle) {
    if (modeMismatch || rejectIfBlocked()) return;
    if (bundle.outOfStock) {
      toast.error('Paket ini sedang tidak tersedia.');
      return;
    }
    // FLEXIBLE bundles let the cashier pick items; FIXED bundles add directly.
    if (bundle.bundleType === 'FLEXIBLE') {
      setBundleConfig(bundle);
      return;
    }
    addBundle(bundle, { unitPrice: bundlePrices[bundle.id]?.price });
    toast.success(`${bundle.name} ditambahkan.`);
  }

  // Catalog only sets date + quantity; slots/add-ons/names are configured on
  // the next step (/details), mirroring the online FE's Add-ons & Slots page.
  function selectProduct(product: Product) {
    if (modeMismatch || rejectIfBlocked()) return;
    addProduct(product, { unitPrice: productPrices[product.id] });
    toast.success(`${product.name} ditambahkan.`);
  }

  // ─── Hotkeys ──────────────────────────────────────────────────────────────
  useHotkeys({
    f2: () => searchRef.current?.focus(),
    f4: () => lineCount > 0 && !modeMismatch && !rejectIfBlocked() && navigate('/details'),
    'ctrl+l': () => navigate('/orders'),
    'ctrl+n': () => {
      if (lineCount > 0) {
        if (window.confirm('Kosongkan keranjang?')) clearCart();
      }
    },
  });

  // ─── Barcode scanner ─ match SKU → add to cart ──────────────────────────────
  useBarcodeScanner({
    onScan: (code) => {
      if (rejectIfBlocked()) return;
      const matches = (p: Product) =>
        p.sku?.toLowerCase() === code.toLowerCase() || p.id === code;
      const match = products.find(matches);
      if (!match) {
        // A real ticket the day's catalog left out (no slot left, closed that
        // day) — say so instead of "not found".
        const offToday = allProducts.find(matches);
        if (offToday) {
          toast.error(`${offToday.name} — tidak tersedia untuk tanggal ini.`);
          return;
        }
        // An FOC ticket's barcode is real but never sold here — say where it is.
        const focMatch = (data?.items ?? []).find(
          (p) => isFoc(p) && (p.sku?.toLowerCase() === code.toLowerCase() || p.id === code),
        );
        if (focMatch) {
          toast.error('Tiket FOC dijual lewat halaman Tiket FOC.');
          return;
        }
        setQuery(code);
        toast.message('Produk tidak ditemukan untuk barcode tersebut.');
        return;
      }
      // Respect availability (verdict unknown while loading → allow; the BE still
      // guards capacity at sale time).
      const verdict = availabilityByProduct[match.id];
      if (verdict?.state === 'unavailable') {
        toast.error(
          verdict.reason === 'expired'
            ? `${match.name} — waktu tiket sudah lewat untuk tanggal ini.`
            : `${match.name} — stok habis.`,
        );
        return;
      }
      selectProduct(match);
    },
  });

  return (
    <div className="flex h-full">
      {/* Work area */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="space-y-3 border-b border-border bg-cream-50/70 px-6 py-4">
          {/* Row 1 — cashier identity + date + search */}
          <div className="flex flex-wrap items-center gap-3">
            <CashierInfo />
            <div className="ml-auto flex flex-1 flex-wrap items-center justify-end gap-3">
              <VisitDatePicker value={visitDate} onChange={setVisitDate} />
              <div className="min-w-[220px] flex-1">
                <ProductSearchBar ref={searchRef} value={query} onChange={setQuery} />
              </div>
            </div>
          </div>

          {visitBlock && <BlockedDateBanner block={visitBlock} visitDate={visitDate} />}

          {/* Row 2 — catalog heading + Semua/Tiket/Paket filter */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-base font-bold leading-none tracking-tight text-ink-900">
              Katalog Tiket
            </h1>
            <Tabs value={filter} onValueChange={(v) => setFilter(v as CatalogFilter)}>
              <TabsList>
                <TabsTrigger value="all">Semua</TabsTrigger>
                <TabsTrigger value="tickets">Tiket</TabsTrigger>
                <TabsTrigger value="bundles">Paket</TabsTrigger>
              </TabsList>
            </Tabs>
            <span className="ml-auto text-xs text-ink-400">{countLabel}</span>
          </div>
        </div>

        <ScrollArea className="flex-1 px-6 py-5">
          <div className="space-y-8">
            {showTickets && (
              <section>
                {filter === 'all' && <SectionLabel>Tiket</SectionLabel>}
                <ProductGrid
                  products={filtered}
                  isLoading={isLoading}
                  isError={isError}
                  onSelect={selectProduct}
                  onRetry={() => (fromCatalog ? catalog.refetch() : refetch())}
                  resolvedPrices={productPrices}
                  visitDate={visitDate}
                  blocked={dateBlocked}
                  allHidden={!query.trim() && sellableProducts.length === 0 && hiddenCount > 0}
                />
              </section>
            )}
            {showBundles && (
              <section>
                {filter === 'all' && <SectionLabel>Paket</SectionLabel>}
                <BundleGrid
                  bundles={filteredBundles}
                  isLoading={bundlesLoading}
                  isError={bundlesError}
                  onSelect={selectBundle}
                  onRetry={() => refetchBundles()}
                  resolvedPrices={bundlePrices}
                  blocked={dateBlocked}
                />
              </section>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Persistent cart */}
      <div className="hidden w-[400px] shrink-0 pos:block">
        <CartSidebar onCheckout={() => navigate('/details')} />
      </div>

      {/* FLEXIBLE bundle configuration */}
      <BundleBuilder
        open={Boolean(bundleConfig)}
        onOpenChange={(o) => !o && setBundleConfig(null)}
        bundle={bundleConfig}
        onConfirm={(item) => {
          if (bundleConfig && !rejectIfBlocked()) {
            addBundle(bundleConfig, { selectedItems: item.selectedItems });
            toast.success(`${bundleConfig.name} ditambahkan.`);
          }
          setBundleConfig(null);
        }}
      />

      {modeDialog}
    </div>
  );
}
